<template>
  <v-container fluid class="pa-2">
    <!-- Header -->
    <v-card class="mb-3" elevation="2">
      <v-card-title class="primary white--text py-3">
        <v-icon left color="white">mdi-car-search</v-icon>
        <span class="text-h6">
          {{ isEditMode ? 'Editar' : 'Nueva' }} Inspección Vehicular
        </span>
        <v-chip v-if="isEditMode" small class="ml-2" color="white" outlined>
          #{{ inspection.id }}
        </v-chip>
      </v-card-title>
    </v-card>

    <!-- Form -->
    <v-form ref="inspectionForm" v-model="valid">
      <!-- Datos del Cliente y Vehículo -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-account-card</v-icon>
          Información General
        </v-card-title>
        <v-card-text class="py-2">
          <v-row dense>
            <!-- Datos del Cliente -->
            <v-col cols="12" md="6">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold py-2 px-3">
                  Datos del Cliente
                </v-card-subtitle>
                <v-card-text class="py-2 px-3">
                  <v-text-field
                    v-model="inspection.cliente_nombre"
                    label="Nombre del Cliente *"
                    prepend-icon="mdi-account"
                    dense
                    :rules="[rules.required]"
                    hide-details="auto"
                    class="mb-2"
                  ></v-text-field>
                  <v-text-field
                    v-model="inspection.cliente_documento"
                    label="Documento"
                    prepend-icon="mdi-card-account-details"
                    dense
                    hide-details="auto"
                    class="mb-2"
                  ></v-text-field>
                  <v-text-field
                    v-model="inspection.cliente_telefono"
                    label="Teléfono"
                    prepend-icon="mdi-phone"
                    dense
                    hide-details="auto"
                  ></v-text-field>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Datos del Vehículo -->
            <v-col cols="12" md="6">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold py-2 px-3">
                  Datos del Vehículo
                </v-card-subtitle>
                <v-card-text class="py-2 px-3">
                  <v-text-field
                    v-model="inspection.vehiculo_placa"
                    label="Placa *"
                    prepend-icon="mdi-card-text"
                    dense
                    :rules="[rules.required]"
                    hide-details="auto"
                    class="mb-2"
                  ></v-text-field>

                  <v-row dense>
                    <v-col cols="6">
                      <v-text-field
                        v-model="inspection.vehiculo_marca"
                        label="Marca"
                        dense
                        hide-details="auto"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model="inspection.vehiculo_modelo"
                        label="Modelo"
                        dense
                        hide-details="auto"
                        class="mb-2"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                  <v-row dense>
                    <v-col cols="4">
                      <v-text-field
                        v-model="inspection.vehiculo_año"
                        label="Año"
                        type="number"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="inspection.vehiculo_color"
                        label="Color"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="4">
                      <v-text-field
                        v-model="inspection.vehiculo_kilometraje"
                        label="Km"
                        type="number"
                        dense
                        hide-details="auto"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Inventario de Condiciones -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-clipboard-list-outline</v-icon>
          Inventario y Control
        </v-card-title>
        <v-card-text class="py-2">
          <v-row dense>
            <!-- EXTERIORES -->
            <v-col cols="12" md="4">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold primary white--text py-2">
                  <v-icon left small color="white">mdi-car-side</v-icon>
                  EXTERIORES
                </v-card-subtitle>
                <v-card-text class="py-1 px-3" style="max-height: 400px; overflow-y: auto;">
                  <v-checkbox
                    v-for="(value, key) in exteriores"
                    :key="key"
                    v-model="exteriores[key]"
                    :label="formatLabel(key)"
                    hide-details
                    dense
                    class="my-0 py-0"
                  ></v-checkbox>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- INTERIORES -->
            <v-col cols="12" md="4">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold info white--text py-2">
                  <v-icon left small color="white">mdi-car-seat</v-icon>
                  INTERIORES
                </v-card-subtitle>
                <v-card-text class="py-1 px-3" style="max-height: 400px; overflow-y: auto;">
                  <v-checkbox
                    v-for="(value, key) in interiores"
                    :key="key"
                    v-model="interiores[key]"
                    :label="formatLabel(key)"
                    hide-details
                    dense
                    class="my-0 py-0"
                  ></v-checkbox>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- ACCESORIOS -->
            <v-col cols="12" md="4">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold success white--text py-2">
                  <v-icon left small color="white">mdi-toolbox</v-icon>
                  ACCESORIOS
                </v-card-subtitle>
                <v-card-text class="py-1 px-3" style="max-height: 400px; overflow-y: auto;">
                  <v-checkbox
                    v-for="(value, key) in accesorios"
                    :key="key"
                    v-model="accesorios[key]"
                    :label="formatLabel(key)"
                    hide-details
                    dense
                    class="my-0 py-0"
                  ></v-checkbox>
                  
                  <v-divider class="my-2"></v-divider>
                  
                  <div class="subtitle-2 font-weight-bold mb-1">OTROS</div>
                  <v-checkbox
                    v-for="(value, key) in otros"
                    :key="key"
                    v-model="otros[key]"
                    :label="formatLabel(key)"
                    hide-details
                    dense
                    class="my-0 py-0"
                  ></v-checkbox>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Estado General y Nivel de Combustible -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-gauge</v-icon>
          Estado General del Vehículo
        </v-card-title>
        <v-card-text class="py-2">
          <v-row dense>
            <!-- Nivel de Combustible -->
            <v-col cols="12" md="4">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold py-2">
                  Nivel de Combustible
                </v-card-subtitle>
                <v-card-text class="py-2">
                  <div class="text-center">
                    <v-icon :size="80" :color="getFuelColor()">mdi-gauge</v-icon>
                    <div class="text-h5 font-weight-bold">{{ inspection.nivel_combustible }}%</div>
                  </div>
                  <v-slider
                    v-model="inspection.nivel_combustible"
                    :min="0"
                    :max="100"
                    :step="10"
                    thumb-label
                    :color="getFuelColor()"
                    track-color="grey lighten-2"
                    class="mt-2"
                    hide-details
                  >
                    <template v-slot:prepend>
                      <v-icon small>mdi-gas-station-off</v-icon>
                    </template>
                    <template v-slot:append>
                      <v-icon small>mdi-gas-station</v-icon>
                    </template>
                  </v-slider>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- Fotos del Vehículo -->
            <v-col cols="12" md="8">
              <v-card outlined flat>
                <v-card-subtitle class="subtitle-2 font-weight-bold py-2">
                  Fotografías del Vehículo
                </v-card-subtitle>
                <v-card-text class="py-2">
                  <v-file-input
                    v-model="newPhotos"
                    label="Agregar Fotos"
                    prepend-icon="mdi-camera"
                    multiple
                    accept="image/*"
                    dense
                    @change="handlePhotoUpload"
                    hide-details="auto"
                    class="mb-2"
                  ></v-file-input>
                  
                  <v-row dense v-if="fotografias.length > 0">
                    <v-col
                      v-for="(photo, index) in fotografias"
                      :key="index"
                      cols="6"
                      sm="4"
                      md="3"
                    >
                      <v-card outlined flat>
                        <v-img
                          :src="photo.url"
                          aspect-ratio="1.5"
                          class="grey lighten-2"
                          height="100"
                        >
                          <template v-slot:placeholder>
                            <v-row class="fill-height ma-0" align="center" justify="center">
                              <v-progress-circular indeterminate size="30"></v-progress-circular>
                            </v-row>
                          </template>
                        </v-img>
                        <v-card-actions class="pa-1">
                          <v-btn icon x-small @click="removePhoto(index)" color="error">
                            <v-icon x-small>mdi-delete</v-icon>
                          </v-btn>
                          <v-spacer></v-spacer>
                          <span class="caption text-truncate">{{ photo.descripcion }}</span>
                        </v-card-actions>
                      </v-card>
                    </v-col>
                  </v-row>
                  <div v-else class="text-center grey--text py-3">
                    <v-icon color="grey lighten-1">mdi-image-off</v-icon>
                    <div class="caption">No hay fotos adjuntas</div>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Diagrama del Vehículo -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-car-outline</v-icon>
          Diagrama y Observaciones de Daños
        </v-card-title>
        <v-card-text class="py-2">
          <vehicle-diagram
            v-model="diagrama_vehiculo"
            @update="handleDiagramUpdate"
          ></vehicle-diagram>
        </v-card-text>
      </v-card>

      <!-- Observaciones -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-text-box</v-icon>
          Observaciones
        </v-card-title>
        <v-card-text class="py-2">
          <v-textarea
            v-model="inspection.observaciones"
            label="Observaciones de Orden General"
            dense
            rows="3"
            placeholder="Escriba aquí cualquier observación adicional sobre el estado del vehículo..."
            hide-details
          ></v-textarea>
        </v-card-text>
      </v-card>

      <!-- Autorizaciones y Firmas -->
      <v-card class="mb-3" elevation="2">
        <v-card-title class="subtitle-2 font-weight-bold py-2">
          <v-icon left small>mdi-pen</v-icon>
          Autorizaciones y Firmas
        </v-card-title>
        <v-card-text class="py-2">
          <v-checkbox
            v-model="inspection.prueba_ruta"
            label="Autorizo realizar prueba de ruta con el vehículo"
            hide-details
            dense
            class="mt-0 mb-2"
          ></v-checkbox>
          
          <v-row dense>
            <!-- Firma del Cliente -->
            <v-col cols="12" md="6">
              <signature-pad
                v-model="inspection.firma_cliente"
                label="Firma del Cliente"
                @update="handleClientSignature"
              ></signature-pad>
            </v-col>

            <!-- Firma del Inspector -->
            <v-col cols="12" md="6">
              <signature-pad
                v-model="inspection.firma_inspector"
                label="Firma del Inspector"
                @update="handleInspectorSignature"
              ></signature-pad>
            </v-col>
          </v-row>

          <v-text-field
            v-model="inspection.inspector"
            label="Nombre del Inspector *"
            prepend-icon="mdi-account-tie"
            dense
            :rules="[rules.required]"
            hide-details="auto"
            class="mt-2"
          ></v-text-field>
        </v-card-text>
      </v-card>

      <!-- Botones de Acción -->
      <v-card elevation="2">
        <v-card-actions class="pa-3">
          <v-spacer></v-spacer>
          <v-btn
            color="grey"
            text
            @click="clearForm"
            :disabled="loading"
          >
            <v-icon left small>mdi-close</v-icon>
            Cancelar
          </v-btn>
          <v-btn
            color="info"
            text
            @click="saveAsDraft"
            :loading="loading"
            :disabled="loading"
          >
            <v-icon left small>mdi-content-save-outline</v-icon>
            Borrador
          </v-btn>
          <v-btn
            color="primary"
            @click="saveInspection"
            :loading="loading"
            :disabled="loading"
          >
            <v-icon left small>mdi-check-circle</v-icon>
            Guardar
          </v-btn>
          <v-btn
            v-if="inspection.id"
            color="success"
            @click="generatePDF"
            :loading="generatingPDF"
            :disabled="loading"
          >
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Imprimir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-form>

    <!-- Loading Dialog -->
    <v-dialog v-model="loading" persistent width="300">
      <v-card color="primary" dark>
        <v-card-text class="pt-4">
          <div class="text-center">
            <v-progress-circular
              indeterminate
              color="white"
              class="mb-3"
              size="50"
            ></v-progress-circular>
            <div class="text-h6">{{ loadingStatus.title }}</div>
            <div class="text-body-2">{{ loadingStatus.text }}</div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Snackbar para mensajes -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
      right
    >
      <v-icon left dark small>{{ snackbar.icon }}</v-icon>
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          icon
          small
          dark
          v-bind="attrs"
          @click="snackbar.show = false"
        >
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Hidden Print Template - FORMATO COMPACTO COMO LA IMAGEN -->
    <div id="printTemplate" style="display: none;">
      <div style="max-width: 100%; margin: 0; padding: 10px; font-family: Arial, sans-serif; font-size: 10px;">
        
        <!-- Header Compacto -->
        <div style="text-align: center; border: 2px solid #000; padding: 5px; margin-bottom: 5px;">
          <div style="font-size: 14px; font-weight: bold; margin: 0;">INSPECCIÓN Y CONTROL DE CONDICIONES GENERALES DEL VEHÍCULO</div>
          <div style="font-size: 9px; margin: 2px 0;">Inspección #{{ inspection.id }} - {{ formatDateForPrint(inspection.fecha) }}</div>
        </div>

        <!-- Cliente y Vehículo en tabla compacta -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 5px; font-size: 9px;">
          <tr>
            <td style="border: 1px solid #000; padding: 3px; width: 50%;"><strong>Cliente:</strong> {{ inspection.cliente_nombre }}</td>
            <td style="border: 1px solid #000; padding: 3px; width: 25%;"><strong>Documento:</strong> {{ inspection.cliente_documento }}</td>
            <td style="border: 1px solid #000; padding: 3px; width: 25%;"><strong>Tel:</strong> {{ inspection.cliente_telefono }}</td>
          </tr>
          <tr>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Placa:</strong> {{ inspection.vehiculo_placa }}</td>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Marca:</strong> {{ inspection.vehiculo_marca }}</td>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Modelo:</strong> {{ inspection.vehiculo_modelo }}</td>
          </tr>
          <tr>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Año:</strong> {{ inspection.vehiculo_año }}</td>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Color:</strong> {{ inspection.vehiculo_color }}</td>
            <td style="border: 1px solid #000; padding: 3px;"><strong>Km:</strong> {{ inspection.vehiculo_kilometraje }}</td>
          </tr>
        </table>

        <!-- Inventario - Formato tabla compacta como la imagen -->
        <div style="border: 2px solid #000; padding: 5px; margin-bottom: 5px;">
          <div style="text-align: center; font-weight: bold; font-size: 10px; margin-bottom: 3px; border-bottom: 1px solid #000; padding-bottom: 2px;">
            INVENTARIO Y CONTROL DE CONDICIONES GENERALES DEL VEHÍCULO
          </div>
          
          <table style="width: 100%; border-collapse: collapse; font-size: 8px;">
            <thead>
              <tr style="background-color: #e0e0e0;">
                <th style="border: 1px solid #000; padding: 2px; text-align: left; width: 28%;">EXTERIORES</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">SÍ</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">NO</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: left; width: 28%;">INTERIORES</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">SÍ</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">NO</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: left; width: 26%;">ACCESORIOS</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">SÍ</th>
                <th style="border: 1px solid #000; padding: 2px; text-align: center; width: 3%;">NO</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="index in 15" :key="index">
                <td style="border: 1px solid #000; padding: 1px 3px;">{{ getExterioresItem(index - 1) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getExterioresCheck(index - 1, true) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getExterioresCheck(index - 1, false) }}</td>
                <td style="border: 1px solid #000; padding: 1px 3px;">{{ getInterioresItem(index - 1) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getInterioresCheck(index - 1, true) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getInterioresCheck(index - 1, false) }}</td>
                <td style="border: 1px solid #000; padding: 1px 3px;">{{ getAccesoriosItem(index - 1) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getAccesoriosCheck(index - 1, true) }}</td>
                <td style="border: 1px solid #000; padding: 1px; text-align: center;">{{ getAccesoriosCheck(index - 1, false) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Diagrama y Nivel de Combustible -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 5px;">
          <tr>
            <td style="border: 1px solid #000; padding: 5px; width: 60%; vertical-align: top;">
              <div style="text-align: center; font-weight: bold; font-size: 9px; margin-bottom: 3px;">OBSERVACIONES DE ORDEN GENERAL Y ESTADO</div>
              <div style="min-height: 80px; font-size: 8px; white-space: pre-wrap;">{{ inspection.observaciones || 'Sin observaciones' }}</div>
            </td>
            <td style="border: 1px solid #000; padding: 5px; width: 40%; text-align: center; vertical-align: top;">
              <div style="font-weight: bold; font-size: 9px; margin-bottom: 3px;">NIVEL DE COMBUSTIBLE</div>
              <div style="font-size: 24px; margin: 10px 0;">{{ getFuelGauge() }}</div>
              <div style="font-size: 12px; font-weight: bold;">{{ inspection.nivel_combustible }}%</div>
            </td>
          </tr>
        </table>

        <!-- Notas importantes y autorización -->
        <div style="border: 1px solid #000; padding: 3px; margin-bottom: 5px; font-size: 7px;">
          <div style="text-align: center; font-weight: bold; margin-bottom: 2px;">
            ESTA ORDEN SERVIRÁ DE SOPORTE PARA LA POSTERIOR EMISIÓN DE LA FACTURA ELECTRÓNICA CONFORME A LA NORMATIVIDAD VIGENTE DE LA DIAN
          </div>
          <div style="margin-bottom: 2px;">
            <strong>RESPONSABILIDAD:</strong> La empresa no se hace responsable por objetos personales dejados en el vehículo, todo servicio se realiza conforme al diagnóstico y autorización del cliente.
          </div>
        </div>

        <!-- Autorización -->
        <div style="border: 1px solid #000; padding: 3px; margin-bottom: 5px; font-size: 9px;">
          <strong>EL CLIENTE AUTORIZA PRUEBA DE RUTA SÍ:</strong> 
          <span style="display: inline-block; width: 20px; height: 15px; border: 1px solid #000; text-align: center; margin: 0 5px;">
            {{ inspection.prueba_ruta ? 'X' : '' }}
          </span>
          <strong>NO:</strong>
          <span style="display: inline-block; width: 20px; height: 15px; border: 1px solid #000; text-align: center; margin: 0 5px;">
            {{ !inspection.prueba_ruta ? 'X' : '' }}
          </span>
        </div>

        <!-- Firmas -->
        <table style="width: 100%; border-collapse: collapse; font-size: 9px;">
          <tr>
            <td style="width: 50%; text-align: center; padding: 5px; vertical-align: bottom;">
              <div style="border-top: 1px solid #000; padding-top: 3px; margin-top: 40px;">
                <strong>FIRMA DEL CLIENTE</strong><br>
                <span style="font-size: 8px;">{{ inspection.cliente_nombre }}</span>
              </div>
            </td>
            <td style="width: 50%; text-align: center; padding: 5px; vertical-align: bottom;">
              <div style="border-top: 1px solid #000; padding-top: 3px; margin-top: 40px;">
                <strong>FIRMA DEL INSPECTOR</strong><br>
                <span style="font-size: 8px;">{{ inspection.inspector }}</span>
              </div>
            </td>
          </tr>
        </table>

        <!-- Footer -->
        <div style="margin-top: 5px; text-align: center; font-size: 7px; color: #666;">
          Documento generado el {{ new Date().toLocaleString('es-ES') }}
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
import { webserver } from '../services/webserver.js';
import VehicleDiagram from '../components/VehicleDiagram.vue';
import SignaturePad from '../components/SignaturePad.vue';

export default {
  name: 'VehicleInspection',
  
  components: {
    VehicleDiagram,
    SignaturePad
  },
  
  data() {
    return {
      valid: false,
      loading: false,
      generatingPDF: false,
      isEditMode: false,
      
      loadingStatus: {
        title: '',
        text: ''
      },
      
      // Validation rules
      rules: {
        required: value => !!value || 'Campo requerido'
      },
      
      // Main inspection object
      inspection: {
        id: null,
        fecha: new Date().toISOString(),
        inspector: '',
        cliente_nombre: '',
        cliente_documento: '',
        cliente_telefono: '',
        vehiculo_placa: '',
        vehiculo_marca: '',
        vehiculo_modelo: '',
        vehiculo_año: new Date().getFullYear(),
        vehiculo_color: '',
        vehiculo_kilometraje: 0,
        nivel_combustible: 50,
        observaciones: '',
        prueba_ruta: false,
        firma_cliente: '',
        firma_inspector: '',
        estado: 'borrador'
      },
      
      // Separate objects for checkboxes
      exteriores: {
        luces_principales: false,
        luces_traseras: false,
        luz_stop_guiñadores: false,
        antena_radio: false,
        limpia_parabrisas: false,
        espejo_lateral_izquierdo: false,
        espejo_lateral_derecho: false,
        vidrios_laterales: false,
        parabrisas_ventana_trasera: false,
        tapones_llantas: false,
        tapon_gasolina: false,
        carroceria_sin_golpes: false,
        parachoque_delantero: false,
        parachoque_trasero: false,
        placas_delantera_trasera: false
      },
      
      interiores: {
        calefaccion: false,
        radio_cd: false,
        bocinas: false,
        encendedor: false,
        espejo_retrovisor: false,
        ceniceros: false,
        cinturones: false,
        manijas_vidrios: false,
        pisos_goma: false,
        tapetes: false,
        funda_asientos: false,
        jalador_puertas: false,
        sujetador_manos: false
      },
      
      accesorios: {
        gato: false,
        llave_rueda: false,
        estuche_llaves: false,
        triangulo: false,
        llanta_repuesto: false,
        extintor: false,
        botiquin: false
      },
      
      otros: {
        soat: false,
        inspeccion_tecnica: false
      },
      
      diagrama_vehiculo: {
        marcas_daños: []
      },
      
      fotografias: [],
      newPhotos: [],
      
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        icon: 'mdi-check-circle'
      }
    };
  },
  
  mounted() {
    this.initializeComponent();
  },
  
  methods: {
    initializeComponent() {
      // Check if inspection data was passed directly through router params
      if (this.$route.params.inspectionData) {
        this.populateInspectionData(this.$route.params.inspectionData);
        this.isEditMode = true;
      } else if (this.$route.params.id) {
        this.loadInspection(this.$route.params.id);
        this.isEditMode = true;
      }
    },
    
    populateInspectionData(data) {
      try {
        // Basic inspection data
        this.inspection.id = data.id;
        this.inspection.fecha = data.fecha || new Date().toISOString();
        this.inspection.inspector = data.inspector || '';
        this.inspection.cliente_nombre = data.cliente_nombre || '';
        this.inspection.cliente_documento = data.cliente_documento || '';
        this.inspection.cliente_telefono = data.cliente_telefono || '';
        this.inspection.vehiculo_placa = data.vehiculo_placa || '';
        this.inspection.vehiculo_marca = data.vehiculo_marca || '';
        this.inspection.vehiculo_modelo = data.vehiculo_modelo || '';
        this.inspection.vehiculo_año = data.vehiculo_año || new Date().getFullYear();
        this.inspection.vehiculo_color = data.vehiculo_color || '';
        this.inspection.vehiculo_kilometraje = data.vehiculo_kilometraje || 0;
        this.inspection.nivel_combustible = data.nivel_combustible || 50;
        this.inspection.observaciones = data.observaciones || '';
        this.inspection.prueba_ruta = data.prueba_ruta === 1 || data.prueba_ruta === true;
        this.inspection.firma_cliente = data.firma_cliente || '';
        this.inspection.firma_inspector = data.firma_inspector || '';
        this.inspection.estado = data.estado || 'borrador';
        
        // Parse JSON fields
        if (data.exteriores) {
          this.exteriores = typeof data.exteriores === 'string' 
            ? JSON.parse(data.exteriores) 
            : data.exteriores;
        }
        
        if (data.interiores) {
          this.interiores = typeof data.interiores === 'string'
            ? JSON.parse(data.interiores)
            : data.interiores;
        }
        
        if (data.accesorios) {
          this.accesorios = typeof data.accesorios === 'string'
            ? JSON.parse(data.accesorios)
            : data.accesorios;
        }
        
        if (data.otros) {
          this.otros = typeof data.otros === 'string'
            ? JSON.parse(data.otros)
            : data.otros;
        }
        
        if (data.diagrama_vehiculo) {
          this.diagrama_vehiculo = typeof data.diagrama_vehiculo === 'string'
            ? JSON.parse(data.diagrama_vehiculo)
            : data.diagrama_vehiculo;
        }
        
        if (data.fotografias) {
          this.fotografias = typeof data.fotografias === 'string'
            ? JSON.parse(data.fotografias)
            : data.fotografias;
        }
        
        this.showNotification('Inspección cargada exitosamente', 'success');
      } catch (error) {
        console.error('Error populating inspection data:', error);
        this.showNotification('Error al procesar los datos de la inspección', 'error');
      }
    },
    
    async loadInspection(id) {
      try {
        const storeId = this.getStoreId();
        if (!storeId) {
          throw new Error('No store ID available');
        }
        
        const query = {
          store: storeId,
          table: 'vehicle_inspections',
          filters: [{ field: 'id', operator: '=', value: id }]
        };
        
        webserver('get_dataTable', query, (response) => {
          if (response && response.error) {
            this.showNotification('Error al cargar la inspección', 'error');
          } else if (response && Array.isArray(response) && response.length > 0) {
            this.populateInspectionData(response[0]);
          }
        });
      } catch (error) {
        console.error('Error loading inspection:', error);
        this.showNotification('Error al cargar la inspección', 'error');
      }
    },
    
    saveInspection() {
      this.itemSubmit('completado');
    },
    
    saveAsDraft() {
      this.itemSubmit('borrador');
    },
    
    itemSubmit(estado) {
      // Validate form
      this.valid = this.$refs.inspectionForm.validate();
      
      if (!this.valid) {
        this.showNotification('Complete todos los campos obligatorios', 'error');
        return;
      }
      
      // Prepare item to submit
      let itemToSubmit = { ...this.inspection };
      
      // Add estado
      itemToSubmit.estado = estado;
      
      // Add JSON fields
      itemToSubmit.exteriores = JSON.stringify(this.exteriores);
      itemToSubmit.interiores = JSON.stringify(this.interiores);
      itemToSubmit.accesorios = JSON.stringify(this.accesorios);
      itemToSubmit.otros = JSON.stringify(this.otros);
      itemToSubmit.diagrama_vehiculo = JSON.stringify(this.diagrama_vehiculo);
      itemToSubmit.fotografias = JSON.stringify(this.fotografias);
      
      // Convert boolean to int for prueba_ruta
      itemToSubmit.prueba_ruta = itemToSubmit.prueba_ruta ? 1 : 0;
      
      // Build query
      const qry = {
        store: this.getStoreId(),
        table: 'vehicle_inspections',
        data: [itemToSubmit]
      };
      
      // Set loading status
      this.loadingStatus = { 
        title: 'Guardando', 
        text: estado === 'completado' ? 'Guardando inspección...' : 'Guardando borrador...'
      };
      this.loading = true;
      
      // Save to database
      webserver('put_dataTable', qry, (response) => {
        this.loading = false;
        
        if (response && response.error) {
          this.showNotification('Error al guardar: ' + response.message, 'error');
        } else {
          // Update ID if it's a new record
          if (!this.inspection.id && response.insert_id) {
            this.inspection.id = response.insert_id;
            this.isEditMode = true;
          }
          
          const message = estado === 'completado' 
            ? 'Inspección guardada correctamente' 
            : 'Borrador guardado correctamente';
          
          this.showNotification(message, 'success');
          
          // Redirect to list after save if completed
          if (estado === 'completado') {
            setTimeout(() => {
              this.$router.push({ name: 'VehicleInspectionList' });
            }, 1500);
          }
        }
      });
    },
    
    // Métodos para el formato de impresión compacto
    getExterioresItem(index) {
      const keys = Object.keys(this.exteriores);
      if (index >= keys.length) return '';
      return this.formatLabel(keys[index]);
    },
    
    getExterioresCheck(index, isYes) {
      const keys = Object.keys(this.exteriores);
      if (index >= keys.length) return '';
      const value = this.exteriores[keys[index]];
      return (isYes && value) || (!isYes && !value) ? 'X' : '';
    },
    
    getInterioresItem(index) {
      const keys = Object.keys(this.interiores);
      if (index >= keys.length) return '';
      return this.formatLabel(keys[index]);
    },
    
    getInterioresCheck(index, isYes) {
      const keys = Object.keys(this.interiores);
      if (index >= keys.length) return '';
      const value = this.interiores[keys[index]];
      return (isYes && value) || (!isYes && !value) ? 'X' : '';
    },
    
    getAccesoriosItem(index) {
      const accesoriosKeys = Object.keys(this.accesorios);
      const otrosKeys = Object.keys(this.otros);
      const allKeys = [...accesoriosKeys, ...otrosKeys];
      
      if (index >= allKeys.length) return '';
      
      const key = allKeys[index];
      if (index < accesoriosKeys.length) {
        return this.formatLabel(key);
      } else {
        return this.formatLabel(key);
      }
    },
    
    getAccesoriosCheck(index, isYes) {
      const accesoriosKeys = Object.keys(this.accesorios);
      const otrosKeys = Object.keys(this.otros);
      const allKeys = [...accesoriosKeys, ...otrosKeys];
      
      if (index >= allKeys.length) return '';
      
      const key = allKeys[index];
      let value;
      
      if (index < accesoriosKeys.length) {
        value = this.accesorios[key];
      } else {
        value = this.otros[key];
      }
      
      return (isYes && value) || (!isYes && !value) ? 'X' : '';
    },
    
    getFuelGauge() {
      const level = this.inspection.nivel_combustible;
      if (level <= 25) return 'E ────●';
      if (level <= 50) return 'E ──●──';
      if (level <= 75) return '─●──── F';
      return '────●─ F';
    },
    
    generatePDF() {
      if (!this.inspection.id) {
        this.showNotification('Debe guardar la inspección antes de imprimir', 'warning');
        return;
      }
      
      try {
        this.generatingPDF = true;
        
        // Get the print template
        const printContent = document.getElementById('printTemplate').innerHTML;
        
        // Create a new window for printing
        const printWindow = window.open('', '_blank');
        
        if (!printWindow) {
          throw new Error('No se pudo abrir la ventana de impresión. Verifique que no esté bloqueada por el navegador.');
        }
        
        // Write the content
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Inspección #${this.inspection.id} - ${this.inspection.vehiculo_placa}</title>
            <style>
              @media print {
                body { margin: 0; padding: 5mm; }
                @page { 
                  size: letter;
                  margin: 10mm;
                }
              }
              body { 
                font-family: Arial, sans-serif; 
                line-height: 1.2;
                font-size: 10px;
              }
              * {
                box-sizing: border-box;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `);
        
        printWindow.document.close();
        
        // Wait for content to load, then print
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
            this.generatingPDF = false;
            this.showNotification('Documento listo para imprimir', 'success');
          }, 250);
        };
        
        // Handle print window close
        printWindow.onafterprint = () => {
          printWindow.close();
        };
        
      } catch (error) {
        this.generatingPDF = false;
        console.error('Error generating PDF:', error);
        this.showNotification('Error al generar el documento: ' + error.message, 'error');
      }
    },
    
    formatLabel(key) {
      return key
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    
    formatDateForPrint(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      } catch (error) {
        return dateString;
      }
    },
    
    getFuelColor() {
      const level = this.inspection.nivel_combustible;
      if (level <= 25) return 'error';
      if (level <= 50) return 'warning';
      return 'success';
    },
    
    async handlePhotoUpload(files) {
      if (!files || files.length === 0) return;
      
      for (const file of files) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.fotografias.push({
            url: e.target.result,
            descripcion: file.name,
            fecha: new Date().toISOString()
          });
        };
        reader.readAsDataURL(file);
      }
      
      this.newPhotos = [];
    },
    
    removePhoto(index) {
      this.fotografias.splice(index, 1);
    },
    
    handleDiagramUpdate(diagramData) {
      this.diagrama_vehiculo = diagramData;
    },
    
    handleClientSignature(signature) {
      this.inspection.firma_cliente = signature;
    },
    
    handleInspectorSignature(signature) {
      this.inspection.firma_inspector = signature;
    },
    
    clearForm() {
      if (confirm('¿Está seguro de que desea cancelar? Se perderán todos los cambios no guardados.')) {
        this.$router.push({ name: 'VehicleInspectionList' });
      }
    },
    
    getStoreId() {
      return window.store?.store_id || window.profile?.store_id || null;
    },
    
    showNotification(message, color) {
      this.snackbar.message = message;
      this.snackbar.color = color;
      this.snackbar.icon = this.getSnackbarIcon(color);
      this.snackbar.show = true;
    },
    
    getSnackbarIcon(color) {
      const icons = {
        success: 'mdi-check-circle',
        error: 'mdi-alert-circle',
        warning: 'mdi-alert',
        info: 'mdi-information'
      };
      return icons[color] || 'mdi-information';
    }
  }
};
</script>

<style scoped>
/* Compact form styles */
.v-card {
  border-radius: 4px;
}

.v-card-title {
  font-size: 0.95rem;
}

.v-card-subtitle {
  font-size: 0.875rem;
}

/* Checkbox spacing */
.v-input--checkbox {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Dense rows */
.row.dense {
  margin: -4px;
}

.row.dense > .col {
  padding: 4px;
}

/* Compact text fields */
.v-text-field.v-text-field--enclosed .v-input__prepend-outer {
  margin-top: 0;
  margin-right: 8px;
}

/* Scrollable checkbox areas */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Print styles */
@media print {
  body * {
    visibility: hidden;
  }
  #printTemplate, #printTemplate * {
    visibility: visible;
  }
  #printTemplate {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
}
</style>
